import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Helmet } from "react-helmet";
import { Config } from "@eagerdog/config";
import { IAddress } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { helperService } from "src/services/helper.service";
import { toast } from "src/components/Toast/ToastManager";
import Checkout from "src/routes/EventRegister/Checkout/Checkout";
import UserAddress from "src/routes/EventRegister/UserAddress/UserAddress";

import styles from "./PaymentRequest.module.scss";

const PaymentRequest: React.FC = () => {
  const { eid } = useParams<{ eid: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [authed, setAuthed] = useState(false);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [stripeOptions, setStripeOptions] = useState<StripeElementsOptions>();
  const [deltaFormatted, setDeltaFormatted] = useState<string>("");
  const [userAddress, setUserAddress] = useState<IAddress>();
  const [userPhone, setUserPhone] = useState<string>();

  // Auth gate — run in useEffect so navigate is called after render, not during it
  useEffect(() => {
    if (!helperService.isLoggedIn()) {
      navigate(`/login?r=${location.pathname}`);
    } else {
      setAuthed(true);
    }
  }, [navigate, location.pathname]);

  // Load payment request data — only runs once we've confirmed auth
  useEffect(() => {
    if (!authed || !eid) return;

    apiService
      .getEventSummaryForHandle(eid)
      .then((eventData) => {
        setEvent(eventData);
        return apiService.findPaymentRequests(eventData._id).then(
          (results) => ({ eventData, results })
        );
      })
      .then(async ({ eventData, results }) => {
        if (!results || results.length === 0) {
          setErrorMessage(
            "No outstanding payment requests found for this event."
          );
          setLoading(false);
          return;
        }

        const { transaction } = results[0];

        try {
          const intentData = await apiService.getPaymentRequestIntent(
            eventData._id,
            transaction._id
          );

          setStripePromise(
            loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", {
              stripeAccount: intentData.account_number,
            })
          );

          if (intentData.client_secret) {
            setStripeOptions({ clientSecret: intentData.client_secret });
          }

          const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: (intentData.currency || "usd").toUpperCase(),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          setDeltaFormatted(formatter.format(intentData.delta_cents / 100));
        } catch (err: any) {
          const status = err.response?.status;
          const errorCode = err.response?.data?.eagerdog_error_code as string | undefined;
          const isAuthError =
            status === 401 ||
            (errorCode && errorCode.startsWith("ERROR.AUTH."));

          if (isAuthError) {
            navigate(`/login?r=${location.pathname}`);
            return;
          }
          setErrorMessage(
            err.response?.data?.message ||
              "This payment request is no longer available."
          );
        }

        setLoading(false);
      })
      .catch((err: any) => {
        const status = err.response?.status;
        const errorCode = err.response?.data?.eagerdog_error_code as string | undefined;
        const isAuthError =
          status === 401 ||
          (errorCode && errorCode.startsWith("ERROR.AUTH."));

        if (isAuthError) {
          navigate(`/login?r=${location.pathname}`);
          return;
        }
        toast.show({
          title: "Payment Request",
          content:
            err.response?.data?.message ||
            "Unable to load payment request. Please try again.",
          duration: 10000,
          type: "fail",
        });
        setLoading(false);
      });
  }, [authed, eid, navigate, location.pathname]);

  // While checking auth, show nothing to avoid flash
  if (!authed) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.PaymentRequest}>
        <div className={styles.inner}>
          <div className={styles.loading}>Loading payment request…</div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={styles.PaymentRequest}>
        <div className={styles.inner}>
          <div className={styles.message}>
            <h2>Payment Request</h2>
            <p>{errorMessage}</p>
            {event && (
              <Link to={`/e/${eid}`} className={styles.backLink}>
                Return to event
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.PaymentRequest}>
      {event && (
        <Helmet>
          <title>{event.title} — Payment Request</title>
        </Helmet>
      )}

      <div className={styles.inner}>
        <div className={styles.breadcrumb}>
          {event && (
            <>
              <Link to={`/e/${eid}`}>{event.title}</Link>
              <span className={styles.sep}>/</span>
            </>
          )}
          <span className={styles.current}>Payment Request</span>
        </div>

        <div className={styles.card}>
          <h2 className={styles.title}>Outstanding Balance</h2>

          {event && (
            <p className={styles.eventName}>{event.title}</p>
          )}

          {deltaFormatted && (
            <div className={styles.amount}>
              <span className={styles.amountLabel}>Amount Due</span>
              <span className={styles.amountValue}>{deltaFormatted}</span>
            </div>
          )}

          {stripePromise && (
            <div className={styles.addressSection}>
              <div className={styles.sectionTitle}>Owner Information</div>
              <UserAddress
                onBehalf={false}
                user={helperService.getUser()}
                onChange={(address: IAddress, phone: string) => {
                  setUserAddress(address);
                  setUserPhone(phone);
                }}
              />
            </div>
          )}

          {stripeOptions && stripePromise && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <Checkout
                redirect={
                  Config.web.REACT_APP_WEB_URL +
                  "/e/" +
                  eid +
                  "?payment_request=succeeded"
                }
                userAddress={userAddress}
                userPhone={userPhone}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentRequest;
